import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { theme } from "./modalStyles";

export default function ModalShell({
  title,
  onClose,
  children,
  maxWidth = 768,
  draggable = false,
  zIndex = 9999,
}) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef(null);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!dragging) return undefined;

    function onMouseMove(event) {
      const origin = dragOrigin.current;
      if (!origin) return;
      setOffset({
        x: origin.offsetX + (event.clientX - origin.startX),
        y: origin.offsetY + (event.clientY - origin.startY),
      });
    }

    function onMouseUp() {
      setDragging(false);
      dragOrigin.current = null;
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  function handleDragStart(event) {
    if (!draggable || event.button !== 0) return;
    event.preventDefault();
    dragOrigin.current = {
      startX: event.clientX,
      startY: event.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
    setDragging(true);
  }

  return createPortal(
    <div
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(15, 23, 42, 0.35)",
        padding: 16,
      }}
    >
      <div
        style={{
          backgroundColor: theme.surface,
          borderRadius: 12,
          border: `1px solid ${theme.border}`,
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          width: "100%",
          maxWidth,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontSize: 12,
          color: theme.text,
          transform: `translate(${offset.x}px, ${offset.y}px)`,
        }}
      >
        <div
          onMouseDown={handleDragStart}
          onDoubleClick={() => setOffset({ x: 0, y: 0 })}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: theme.secondary,
            borderBottom: `1px solid ${theme.border}`,
            padding: "12px 16px",
            flexShrink: 0,
            cursor: draggable ? (dragging ? "grabbing" : "grab") : "default",
            userSelect: "none",
          }}
        >
          <h2
            style={{
              fontWeight: "bold",
              fontSize: 14,
              color: theme.primary,
            }}
          >
            {title}
          </h2>
          <button
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.primary,
              display: "flex",
            }}
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}
